import { useEffect, useState, useCallback } from "react";
import { ScrollText, ShieldAlert, List } from "lucide-react";
import api from "@/lib/api";
import { Empty, blurSuTouch } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";

const AZIONI = ["", "creazione", "modifica", "eliminazione", "consultazione", "presenza", "punteggio", "correzione", "approvazione", "rifiuto", "reset_password", "impostazione_password"];

// Eventi di sicurezza (scritti da backend/sicurezza.py). "consultazione" non è
// qui: non è un evento di sicurezza ma la traccia di chi ha LETTO un'anagrafica
// di minori, e si filtra dalla vista normale.
const AZIONI_SICUREZZA = ["", "login_fallito", "login_riuscito", "rate_limit", "blocco_account",
  "attivazione_riuscita", "attivazione_fallita", "codice_invalidato"];

const ETICHETTE_SICUREZZA = {
  login_fallito: "Login fallito",
  login_riuscito: "Login riuscito",
  rate_limit: "Rate limit superato",
  blocco_account: "Blocco account",
  attivazione_riuscita: "Primo accesso riuscito",
  attivazione_fallita: "Primo accesso fallito",
  codice_invalidato: "Codice invalidato",
  consultazione: "Consultazione",
};

const AZIONE_STYLE = {
  creazione: "badge-pos", modifica: "badge-info", eliminazione: "badge-neg",
  presenza: "badge-warn", punteggio: "badge-warn", correzione: "badge-neg",
  approvazione: "badge-pos", rifiuto: "badge-neg", reset_password: "badge-neg",
  impostazione_password: "badge-info",
  login_riuscito: "badge-pos", login_fallito: "badge-neg",
  rate_limit: "badge-warn", blocco_account: "badge-neg",
  consultazione: "badge-info", attivazione_riuscita: "badge-pos",
  attivazione_fallita: "badge-neg", codice_invalidato: "badge-neg",
};

export default function Registro() {
  const [righe, setRighe] = useState([]);
  const [vista, setVista] = useState("tutto"); // "tutto" | "sicurezza"
  const [azione, setAzione] = useState("");
  const [utente, setUtente] = useState("");
  const [ip, setIp] = useState("");

  const sicurezza = vista === "sicurezza";

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (sicurezza) params.set("solo_sicurezza", "true");
    if (azione) params.set("azione", azione);
    if (utente) params.set("utente", utente);
    if (ip) params.set("ip", ip);
    api.get(`/registro?${params}`).then((r) => setRighe(r.data));
  }, [sicurezza, azione, utente, ip]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  const cambiaVista = (v) => { setVista(v); setAzione(""); setIp(""); };

  const fmtTs = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  const opzioni = sicurezza ? AZIONI_SICUREZZA : AZIONI;

  return (
    <div data-testid="registro-page">
      <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-1">Registro Attività</h1>
      <p className="text-sm text-slate-500 font-semibold mb-5">
        {sicurezza
          ? "Eventi di sicurezza — accessi, blocchi e limiti superati"
          : "Log di sistema — scritture e consultazioni delle anagrafiche"}
      </p>
      {/* I nomi non sono conservati nel registro: vengono risolti al momento
          della lettura. Di chi è stato cancellato resta l'operazione, non
          l'identità. */}
      <p className="text-xs text-slate-400 font-semibold mb-5 -mt-3">
        Conservazione: {sicurezza ? "6 mesi" : "12 mesi (6 per le consultazioni)"}. I nomi delle
        persone rimosse non sono più ricostruibili.
      </p>

      {/* Vista: tutto il registro oppure i soli eventi di sicurezza */}
      <div className="flex gap-2 mb-4">
        <button type="button" onClick={() => cambiaVista("tutto")}
          className={vista === "tutto" ? "btn-primary !py-2" : "btn-ghost !py-2"} data-testid="registro-vista-tutto">
          <List className="h-4 w-4" /> Tutto
        </button>
        <button type="button" onClick={() => cambiaVista("sicurezza")}
          className={sicurezza ? "btn-primary !py-2" : "btn-ghost !py-2"} data-testid="registro-vista-sicurezza">
          <ShieldAlert className="h-4 w-4" /> Sicurezza
        </button>
      </div>

      <div className="flex gap-3 mb-5 flex-wrap">
        <select className="input-app basis-full sm:basis-auto sm:max-w-[220px]" value={azione}
          onChange={(e) => { setAzione(e.target.value); blurSuTouch(e); }} data-testid="registro-azione-select">
          {opzioni.map((a) => (
            <option key={a} value={a}>
              {a ? (ETICHETTE_SICUREZZA[a] || a.replace("_", " ")) : (sicurezza ? "Tutti gli eventi" : "Tutte le azioni")}
            </option>
          ))}
        </select>
        <input className="input-app basis-full sm:basis-auto sm:max-w-[220px]"
          placeholder={sicurezza ? "Filtra per utente o credenziale tentata..." : "Filtra per utente..."}
          value={utente} onChange={(e) => setUtente(e.target.value)} data-testid="registro-utente-input" />
        {sicurezza && (
          <input className="input-app basis-full sm:basis-auto sm:max-w-[200px]" placeholder="Filtra per IP..."
            value={ip} onChange={(e) => setIp(e.target.value)} data-testid="registro-ip-input" />
        )}
      </div>

      {righe.length === 0 ? (
        <Empty icon={sicurezza ? ShieldAlert : ScrollText} text={sicurezza ? "Nessun evento di sicurezza" : "Nessuna voce nel registro"} />
      ) : (
        <div className="card-app divide-y divide-slate-50">
          {righe.map((r) => (
            <div key={r.id} className="px-4 py-3 sm:px-5 sm:py-3.5 sm:flex sm:items-start sm:gap-3" data-testid={`registro-riga-${r.id}`}>
              {/* Su mobile: riga compatta badge + orario; su sm+ il wrapper "scompare"
                  (display: contents) e l'ordine ripristina il layout desktop originale */}
              <div className="flex items-center justify-between gap-2 mb-1 sm:mb-0 sm:contents">
                <span className={`${AZIONE_STYLE[r.azione] || "badge-info"} shrink-0 sm:order-2`}>
                  {ETICHETTE_SICUREZZA[r.azione] || r.azione?.replace("_", " ")}
                </span>
                <p className="text-xs font-bold text-slate-400 shrink-0 sm:order-1 sm:w-28 sm:pt-0.5">{fmtTs(r.timestamp)}</p>
              </div>
              <div className="min-w-0 sm:order-3">
                <p className="text-sm font-semibold text-slate-700">{r.descrizione}</p>
                <p className="text-xs text-slate-400 font-semibold mt-0.5">
                  {r.entita} · {r.utente_nome}
                  {r.ip && <> · IP <span className="font-mono text-slate-500">{r.ip}</span></>}
                </p>
                {r.user_agent && (
                  <p className="text-[11px] text-slate-300 font-semibold mt-0.5 truncate" title={r.user_agent}>{r.user_agent}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
